import type { GradingMode } from '@agavi/blocks';

/**
 * Settings a grading mode takes away, named once for the control AND for every reader.
 *
 * ## The rule
 *
 * A setting the current mode does not offer is **unchecked and inert**, and its stored value is
 * **left alone** so that returning to a mode which does offer it restores the teacher's setup
 * intact. Three parts, and all three are load-bearing:
 *
 * - *unchecked*: `CheckboxRow` forces this on any disabled toggle, so no call site can forget it.
 * - *inert*: every reader of the setting must go through `effectiveSetting`. This file cannot
 *   enforce that, which is why `mode-gated-settings.test.tsx` renders each block with the stored
 *   value on and off and demands the two be identical.
 * - *left alone*: nothing here writes. A mode switch must never clear the stored value.
 *
 * ## Why it exists
 *
 * Greying a control out while it still wore its tick told the teacher the feature was active in a
 * mode that had just taken it away, and gave them no way to say otherwise — the control that
 * would have cleared it was the disabled one. Five blocks then went further and acted on the
 * stored value, printing "Tolerance: ±5°" to students directly beside "Any Heading Accepted".
 *
 * The alternative — clear the value on a mode switch — was rejected. Modes get flipped while a
 * teacher is still deciding what a task is, and a flip that quietly destroys an answer format or
 * a tolerance setup is a worse failure than a stale tick ever was.
 *
 * ## Adding a setting
 *
 * Add the key here, use `settingAvailable` in the control's `disabled`, and `effectiveSetting` in
 * every reader. The contract test iterates this table, so a key added here with a reader missed
 * fails rather than ships.
 */
export const MODE_GATED_SETTINGS = {
  /** The accepted radius/band shown to students. GPS, Compass, Rotation, Tilt. */
  showTolerance: ['graded', 'survey'],
  /** Numerical's name for the same thing. */
  showAcceptedRange: ['graded', 'survey'],
  /** Keyword, FITB: how strictly a typed answer is matched. */
  caseSensitive: ['graded', 'survey'],
  accentSensitive: ['graded', 'survey'],
  /** Numerical's answer-format requirements. */
  requireExponentialNotation: ['graded', 'survey'],
  sigFigsRequired: ['graded', 'survey'],
  /**
   * Has Correct only. It diagnoses a wrong WORKING by mutating the teacher's equation, which
   * presupposes a right answer to have worked towards — and Participation has none, since a Set
   * Answer target is one card's business and not the block's verdict.
   */
  mathErrorEnabled: ['graded'],
  /**
   * Sort: whether the sequence INSIDE each bin is graded as well as the membership.
   *
   * Has Correct only, and for the same reason `mathErrorEnabled` is: it is a claim about what
   * counts as right, and Participation has no right. It is also the one setting here that changes
   * what a stored answer key MEANS rather than how an answer is read, which is why it belongs to
   * a version — see `SortVersion.orderWithinBins`.
   */
  orderWithinBins: ['graded'],
} as const satisfies Record<string, readonly GradingMode[]>;

export type ModeGatedSetting = keyof typeof MODE_GATED_SETTINGS;

/** Whether this mode offers the setting at all — the control's `disabled` is the negation. */
export function settingAvailable(key: ModeGatedSetting, gradingMode: GradingMode): boolean {
  return (MODE_GATED_SETTINGS[key] as readonly GradingMode[]).includes(gradingMode);
}

/**
 * What a READER must use in place of the stored value.
 *
 * The stored value survives the mode; its effect does not. Every surface that acts on one of
 * these settings asks this rather than reading the field, so "the teacher still has it set" and
 * "the block is still doing it" cannot drift apart.
 */
export function effectiveSetting(
  key: ModeGatedSetting, gradingMode: GradingMode, stored: boolean,
): boolean {
  return stored && settingAvailable(key, gradingMode);
}
